/*
 * Current user — derived from the MSAL/Entra active account.
 *
 * This replaces the Phase-0 FAKE_USER stub. In test mode (NON-PROD E2E seam) the
 * active account is the injected synthetic account; for real users it is the
 * account MSAL established during bootstrap(). Either way the shell's TopBar /
 * AppShell receive a real CurrentUser via this hook — the shell itself stays
 * auth-agnostic (it takes `user` as a prop; see packages/web-ui).
 */
import type { AccountInfo } from '@azure/msal-browser';
import { getActiveAccount } from './msal';

// The user contract lives in the shared package so every surface describes its
// user the same way. Re-export for app-local convenience.
import type { CurrentUser } from '@andavo/web-ui';
export type { CurrentUser };

/** Derive 1–2 char initials from a display name or email local-part. */
function deriveInitials(name: string | undefined, email: string): string {
  const source = (name && name.trim()) || email.split('@')[0] || '';
  const parts = source.split(/[\s._-]+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/** Map an MSAL AccountInfo to the shell's CurrentUser contract. */
export function accountToCurrentUser(account: AccountInfo): CurrentUser {
  const email = account.username; // MSAL puts the email/UPN in `username`
  const name = account.name || email;
  return {
    id: account.localAccountId || account.homeAccountId || email,
    name,
    email,
    initials: deriveInitials(account.name, email),
  };
}

/**
 * Returns the authenticated user, or null if there is no active account.
 *
 * Reads module state set during bootstrap (initMsal). Components inside the
 * protected tree always have an account (ProtectedRoute gates on
 * isAuthenticated), so the shell can assert non-null there.
 */
export function useCurrentUser(): CurrentUser | null {
  const account = getActiveAccount();
  return account ? accountToCurrentUser(account) : null;
}
